/* eslint-disable camelcase */
import { useRef, forwardRef, useImperativeHandle, MouseEventHandler } from 'react';

import { Menu, MenuItem, MenuButton, FocusableItem } from '@szhsin/react-menu';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { AccountRefreshTrigger } from 'components/AccountRefreshTrigger/AccountRefreshTrigger';
import Render from 'components/render';
import { WalletDetails } from 'components/WalletDetails';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import useLockedBody from 'hooks/useLockedBody';
import i18n from 'i18n/config';
import { selectIsAuthChannelFailed } from 'selectors/user';
import { useAppSelector } from 'storeHooks';
import { WalletIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';

import PnlAnalyticsButton from '../../PnlAnalyticsButton/PnlAnalyticsButton';
import { TotalEquity } from './computeTotalBalance';
import styles from './index.module.scss';

type CustomMenuButtonProps = {
  open: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
};

const CustomMenuButton = forwardRef<HTMLButtonElement, CustomMenuButtonProps>(
  ({ open, onClick }, ref) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

    useLockedBody(open);

    return (
      <MenuButton
        ref={buttonRef}
        data-testid="switchUserMenuButton"
        className={`${open ? styles.active : ''} ${styles.button}`}
        id="wallet-button"
        onClick={onClick}
      >
        <WalletIcon width="18" height="18" />
      </MenuButton>
    );
  }
);

CustomMenuButton.propTypes = {
  open: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

/* eslint-disable no-nested-ternary */
const menuClassName = ({ state }) =>
  state === 'open'
    ? styles['wallet-menu-container-open']
    : state === 'close'
    ? styles['wallet-menu-container-close']
    : styles['wallet-menu-container'];

const accountRefreshTriggerLabel = i18n.t(
  'headerFooter:accountDataIsUpdatingPeriodically'
);

const WalletMenu = () => {
  const { t } = useTranslation(['headerFooter']);
  const isAuthChannelDisconnected = useAppSelector(selectIsAuthChannelFailed);

  return (
    <Menu
      menuButton={({ open }) => (
        <CustomMenuButton
          data-testid="customSwitchUserMenuButton"
          open={open}
          onClick={() => {
            trackMixpanelEvent('Account icon clicked - All Pages - Header');
          }}
        />
      )}
      menuClassName={menuClassName}
    >
      <Render when={isAuthChannelDisconnected}>
        <AccountRefreshTrigger
          className={styles.accountRefreshTriggerContainer}
          message={accountRefreshTriggerLabel}
        />
      </Render>
      <MenuItem className={styles['total-balance-details']}>
        <Box
          className={styles.headingContainer}
          data-testid="wallet-menu-account-container"
        >
          <div className={styles.heading}>
            <span className={styles.headingText}>
              {t('headerFooter:header.wallet.accountValue')}
            </span>
          </div>
          <PnlAnalyticsButton
            className={styles.analyticsRedirect}
            pageName="Wallet Drawer"
            showAsLink
            showLabel
            highlightLink
          />
        </Box>
        <TotalEquity
          // balancesArray={balanceArray}
          className={styles['total-balance']}
          usdClassName={styles['usd-value']}
          btcClassName={styles['btc-value']}
          hideBTC
        />
      </MenuItem>
      <FocusableItem className={styles.detailsContainer}>
        <WalletDetails />
      </FocusableItem>
    </Menu>
  );
};

export default WalletMenu;
